import React, { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  ThemeProvider,
  createTheme,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import { Edit, Info as InfoIcon } from "@mui/icons-material";
import apiClient from "@lift-admin/utils/apiClient";
import { Toastify } from 'lift-ui';
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Info } from "lucide-react";
import BrandMultiSelect from "./brand-multiselect";
import { CellStyle } from "ag-grid-community";
import { actionLog } from "../../../App";
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";
import { buildAccessListChange } from "@/features/drawer-structure-v2/drawer-parts/accessLogHelpers";

const theme = createTheme({
  palette: {
    primary: {
      main: "#000",
    },
    secondary: {
      main: "rgba(0, 0, 0, 0)",
    },
  },
  typography: {
    fontFamily: '"Figtree", sans-serif',
  },
});

interface ItemsProps {
  toggle: string;
  searchText: string;
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  brands: any[];
}

interface FormStateEdit {
  name: string;
  icon: string;
  brands_exclude: number[];
}

// Custom cell renderer for brand display
const BrandCellRenderer = (props: any) => {
  // Extract data from props
  const { value, data } = props;

  if (
    !data ||
    !data.brands_exclude ||
    !Array.isArray(data.brands_exclude) ||
    data.brands_exclude.length === 0
  ) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <span>--</span>
      </div>
    );
  }

  // Split brands into excluded (negative IDs) and included (positive IDs)
  const brandIds = data.brands_exclude;
  const includedBrands = brandIds.filter((id: number) => id > 0);
  const excludedBrands = brandIds
    .filter((id: number) => id < 0)
    .map((id: number) => -id); // Convert to positive for lookup

  // Function to get brand names
  const getBrandNames = (ids: number[], brands: any[]) => {
    return ids
      .map((id: number) => {
        const brand = brands.find((b) => b.id === id);
        return brand ? brand.name : null;
      })
      .filter(Boolean);
  };

  // Get brand names
  const excludedBrandNames = getBrandNames(
    excludedBrands,
    props.context.brandsList
  );
  const includedBrandNames = getBrandNames(
    includedBrands,
    props.context.brandsList
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        maxHeight: "60px",
        overflow: "auto",
        scrollbarWidth: "thin",
      }}
    >
      {excludedBrandNames.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: "4px",
            flexWrap: "wrap",
            paddingRight: "4px",
          }}
        >
          {excludedBrandNames.map((name: string, index: number) => (
            <Chip
              key={`excluded-${index}`}
              label={name}
              size="small"
              sx={{
                color: "#fff",
                bgcolor: "#d32f2f",
                "& .MuiChip-label": { fontSize: "0.75rem" },
                margin: "2px",
              }}
            />
          ))}
          {includedBrandNames.map((name: string, index: number) => (
            <Chip
              key={`included-${index}`}
              label={name}
              size="small"
              sx={{
                color: "#fff",
                bgcolor: "#2e7d32",
                "& .MuiChip-label": { fontSize: "0.75rem" },
                margin: "2px",
              }}
            />
          ))}
        </Box>
      )}

      {excludedBrandNames.length === 0 && includedBrandNames.length === 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>--</span>
        </div>
      )}
    </div>
  );
};

// Custom header component for Excluded/Include Brands
const CustomHeaderExcludedInclude = () => {
  return (
    <div>
      <span style={{ color: "red" }}>Excluded</span>/
      <span style={{ color: "green" }}>Include</span> Brands
    </div>
  );
};

const Channels = ({
  toggle,
  searchText,
  openDialog,
  brands,
  setOpenDialog,
}: ItemsProps) => {
  const [loadData, setLoadData] = useState<boolean>(false);
  const [drawerItem, setDrawerItems] = useState<Array<any>>([]);
  const [drawerItemBackUp, setDrawerItemsBackup] = useState<Array<any>>([]);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [currentData, setCurrentData] = useState<any>(null);
  const [name, setName] = useState<string | null>();
  const [icon, setIcon] = useState<string | null>();
  const [searchedResults, setSearchedResults] = useState<any>([]);
  const [formStateEdit, setFormStateEdit] = useState<FormStateEdit>({
    name: "",
    icon: "",
    brands_exclude: [],
  });

  const getChannels = async () => {
    setLoadData(true);
    try {
      const res = await apiClient.get(
        "/drawer/v1/admin/drawer/structure/channels"
      );

      const channels = res?.data?.response?.channels.sort(
        (
          a: { created_at: string | number | Date },
          b: { created_at: string | number | Date }
        ) => {
          return (
            (new Date(b.created_at) as any) - (new Date(a.created_at) as any)
          );
        }
      );

      setDrawerItems(channels);
      setDrawerItemsBackup(channels);
    } catch {
      Toastify("error", "Something went wrong");
    } finally {
      setLoadData(false);
    }
  };

  useEffect(() => {
    if (toggle == "channels") getChannels();
  }, [toggle]);

  const rowData = useMemo(() => drawerItem, [drawerItem]);

  // Define cell styles as CellStyle objects
  const centerCellStyle: CellStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const startCellStyle: CellStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  };

  const brandsCellStyle: CellStyle = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    paddingTop: "8px",
  };

  const columnDefs = useMemo(
    () => [
      {
        field: "icon",
        headerName: "Icons",
        width: 250,
        sortable: true,
        resizable: true,
        editable: true,
        flex: 1,
        cellStyle: startCellStyle,
      },
      {
        field: "name",
        headerName: "Channel Name",
        width: 250,
        sortable: true,
        resizable: true,
        editable: true,
        flex: 1,
        cellStyle: startCellStyle,
      },
      {
        field: "brands_exclude",
        headerName: "Excluded/Include Brands",
        width: 300,
        sortable: true,
        resizable: true,
        editable: false,
        cellRenderer: BrandCellRenderer,
        headerComponent: CustomHeaderExcludedInclude,
        flex: 1.5,
        cellStyle: brandsCellStyle,
      },
      {
        field: "created_at",
        headerName: "Created At",
        width: 200,
        sortable: true,
        resizable: true,
        editable: false,
        flex: 1,
        cellStyle: startCellStyle,
        valueFormatter: (params: { value: string }) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        },
      },
      {
        field: "updated_at",
        headerName: "Updated At",
        width: 200,
        sortable: true,
        resizable: true,
        editable: false,
        flex: 1,
        cellStyle: startCellStyle,
        valueFormatter: (params: { value: string }) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        cellStyle: centerCellStyle,
        cellRenderer: (params: any) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Edit
              style={{ cursor: "pointer" }}
              onClick={() => handleEditOpen(params.data)}
            />
          </div>
        ),
      },
    ],
    []
  );

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setName("");
    setIcon("");
  };

  const handleAddChannel = async () => {
    try {
      if (!name || name.length === 0 || !icon || icon.length === 0) {
        Toastify("error", "Please enter correct fields");
        return;
      }
      const res = await apiClient.post(
        "/drawer/v1/admin/drawer/structure/channel",
        {
          name,
          icon,
        }
      );
      getChannels();
      setName("");
      setIcon("");
      setOpenDialog(false);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const channelFieldSpecs: FieldSpec[] = [
        { key: "name", label: "Name" },
        { key: "icon", label: "Icon" },
      ];
      const desc = {
        message: buildSummary("Channel", name, "created"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "create_channel",
        channel_name: name,
        user_id: user?.id,
        user_name: user?.name,
        details: buildDetails({ name, icon }, channelFieldSpecs, {
          skipEmpty: true,
        }),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err) {
      setOpenDialog(false);
      Toastify("error", "Channel not added");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const errorDesc = {
        message: `Failed to create Channel (Channel Name: "${name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: (err as any).response?.status,
        method: "POST",
        url: "/drawer/v1/admin/drawer/structure/channel",
        category: "drawer",
        action: "create_channel",
        channel_name: name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const handleEdit = async () => {
    if (!currentId || isNaN(currentId)) {
      Toastify("error", "Invalid channel ID. Please select correct channel");
      return;
    }

    try {
      const res = await apiClient.put(
        `/drawer/v1/admin/drawer/structure/channel/${currentId}`,
        formStateEdit
      );
      Toastify("success", "Item updated successfully");
      const channelFieldSpecs: FieldSpec[] = [
        { key: "name", label: "Name" },
        { key: "icon", label: "Icon" },
      ];
      const previousChannel = {
        name: currentData?.name,
        icon: currentData?.icon,
      };
      const nextChannel = {
        name: formStateEdit?.name,
        icon: formStateEdit?.icon,
      };
      getChannels();
      handleEditDialogClose();
      const changes = diffFields(
        previousChannel,
        nextChannel,
        channelFieldSpecs
      );
      const brandChange = buildAccessListChange(
        "Brand Access",
        currentData?.brands_exclude || [],
        formStateEdit.brands_exclude || [],
        brands || [],
        "Brand"
      );
      if (brandChange) changes.push(brandChange);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const desc = {
        message: buildSummary(
          "Channel",
          formStateEdit?.name || currentData?.name,
          "updated",
          changes.length
        ),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "update_channel",
        channel_id: currentId,
        channel_name: formStateEdit?.name || currentData?.name,
        user_id: user?.id,
        user_name: user?.name,
        changes,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err) {
      Toastify("error", "Item not updated");
      console.error(err);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const errorDesc = {
        message: `Failed to edit Channel (Channel ID: ${currentId}, Channel Name: "${currentData?.name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: (err as any).response?.status,
        method: "PUT",
        url: `/drawer/v1/admin/drawer/structure/channel/${currentId}`,
        category: "drawer",
        action: "update_channel",
        channel_id: currentId,
        channel_name: currentData?.name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const handleSubmit = () => {
    handleAddChannel();
  };

  const handleEditOpen = (data: any) => {
    setCurrentId(data?.id);
    setCurrentData(data);
    setFormStateEdit({
      name: data?.name,
      icon: data?.icon,
      brands_exclude: data?.brands_exclude || [],
    });
    console.log(formStateEdit, "data?.brands_exclude");
    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setOpenDialog(false);
    setCurrentData(null);
    setFormStateEdit({
      name: "",
      icon: "",
      brands_exclude: [],
    });
    setCurrentId(null);
    setOpenEditDialog(false);
  };

  const searchResults = (query: string) => {
    const filteredUsers = [...drawerItemBackUp];
    const results = filteredUsers.filter((user) =>
      user.name?.toLowerCase().includes(query.toLowerCase())
    );
    setDrawerItems(results);
  };

  useEffect(() => {
    searchResults(searchText);
  }, [searchText, drawerItemBackUp]);

  return (
    <>
      <ThemeProvider theme={theme}>
        <div className="info-container">
          <Info className="info-icon" />
          <p
            style={{
              marginLeft: "0.5rem",
            }}
          >
            Please create a{" "}
            <a href="https://feedback.lift.newengen.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline hover:no-underline">ticket</a>
            {" "}for any additions or modifications
          </p>
        </div>
        <Paper className="table-paper-with-info" style={{ paddingLeft: 0 }}>
          <div
            className="ag-theme-alpine"
            style={{ height: "100%", width: "100%" }}
          >
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rowData}
              defaultColDef={defaultColDef}
              rowHeight={90}
              pagination={true}
              paginationPageSize={20}
              suppressPaginationPanel={false}
              context={{ brandsList: brands }}
            />
          </div>
        </Paper>

        <Dialog
          open={openEditDialog}
          onClose={handleEditDialogClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Channel</DialogTitle>
          <DialogContent style={{ padding: "16px" }}>
            <TextField
              label="Name"
              value={formStateEdit.name}
              onChange={(e) =>
                setFormStateEdit((prev: FormStateEdit) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              fullWidth
              style={{ marginBottom: "16px" }}
            />

            <TextField
              label="Icon"
              value={formStateEdit.icon}
              onChange={(e) =>
                setFormStateEdit((prev: FormStateEdit) => ({
                  ...prev,
                  icon: e.target.value,
                }))
              }
              fullWidth
              style={{ marginBottom: "16px" }}
            />

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Brand Configuration:
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <InfoIcon
                fontSize="small"
                style={{ verticalAlign: "middle", marginRight: "4px" }}
              />
              Use negative IDs (e.g., -1, -2) to exclude specific brands. Use
              positive IDs (e.g., 1, 2) to include only those specific brands.
            </Typography>

            <BrandMultiSelect
              defaultBrands={formStateEdit.brands_exclude}
              setDefaultBrands={(field: string, value: number[]) => {
                setFormStateEdit((prev: FormStateEdit) => ({
                  ...prev,
                  brands_exclude: value,
                }));
              }}
            />

            <Box
              sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="subtitle2">
                Current Selection Preview:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {formStateEdit.brands_exclude
                  .filter((id) => id < 0)
                  .map((id) => {
                    const brandName =
                      brands.find((b) => b.id === -id)?.name ||
                      `Unknown (ID: ${-id})`;
                    return (
                      <Chip
                        key={`preview-excluded-${id}`}
                        label={brandName}
                        size="small"
                        sx={{ bgcolor: "#d32f2f", color: "white" }}
                      />
                    );
                  })}

                {formStateEdit.brands_exclude
                  .filter((id) => id > 0)
                  .map((id) => {
                    const brandName =
                      brands.find((b) => b.id === id)?.name ||
                      `Unknown (ID: ${id})`;
                    return (
                      <Chip
                        key={`preview-included-${id}`}
                        label={brandName}
                        size="small"
                        sx={{ bgcolor: "#2e7d32", color: "white" }}
                      />
                    );
                  })}

                {formStateEdit.brands_exclude.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    All brands will be included (no restrictions)
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditDialogClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleEdit} color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </>
  );
};

export default Channels;
